import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Trophy, Users, Download } from "lucide-react";
import html2canvas from "html2canvas";
import { api } from "@/lib/api";
import { fmtKES as fmtKes } from "@/lib/api";
import { useSortableData } from "@/hooks/useSortableData";
import { SortableTh } from "@/components/ui/sortable-th";
import { signTone, toneClassName } from "@/lib/numberColor";

/**
 * AgencyLeaderboard — private, broker-scoped leaderboard for training
 * competitions. Ranks the broker's own traders on the same metrics as the
 * public board, but the population is filtered to `employees`, `clients`,
 * or `all` under this agency.
 *
 * Backend: GET /api/agency/leaderboard?scope=…&criterion=…&limit=…
 */

const CRITERIA = [
    { key: "net_profit",   label: "Net PnL",     format: (v) => fmtKes(v),   desc: "Rank by total closed + floating PnL" },
    { key: "win_rate",     label: "Win rate",    format: (v) => `${(v || 0).toFixed(1)}%`, desc: "Percentage of profitable trades" },
    { key: "consistency",  label: "Consistency", format: (v) => (v || 0).toFixed(1),      desc: "Std-dev of daily PnL (lower is smoother)" },
    { key: "trades",       label: "Trades",      format: (v) => (v || 0).toString(),      desc: "Total closed trades" },
    { key: "unique_days",  label: "Days traded", format: (v) => (v || 0).toString(),      desc: "Distinct trading days" },
];

export default function AgencyLeaderboard() {
    const [scope, setScope] = useState("employees");
    const [criterion, setCriterion] = useState("net_profit");
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const cardRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/agency/leaderboard", {
                params: { scope, criterion, limit: 25 },
            });
            setRows(r.data.rows || []);
            setTotal(r.data.total || 0);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load leaderboard");
        } finally {
            setLoading(false);
        }
    }, [scope, criterion]);

    useEffect(() => { load(); }, [load]);

    const exportPng = async () => {
        if (!cardRef.current) return;
        try {
            // Use the card's own background so the export looks native to the app.
            const canvas = await html2canvas(cardRef.current, {
                backgroundColor: null, scale: 2, useCORS: true,
            });
            const stamp = new Date().toISOString().slice(0, 10);
            const filename = `melian-leaderboard-${scope}-${stamp}.png`;
            canvas.toBlob((blob) => {
                if (!blob) { toast.error("Export failed"); return; }
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = filename;
                document.body.appendChild(a); a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                toast.success("Leaderboard image saved");
            }, "image/png");
        } catch (e) {
            toast.error("Export failed: " + (e?.message || "unknown"));
        }
    };

    const activeCriterion = CRITERIA.find((c) => c.key === criterion) || CRITERIA[0];
    const { sorted: sortedRows, sortKey, direction, requestSort } = useSortableData(rows, {
        accessors: {
            rank: (r) => Number(r.rank || 0),
            name: (r) => r.name || "",
            type: (r) => r.client_type || "",
            metric: (r) => Number(r[criterion] || 0),
            unique_days: (r) => Number(r.unique_days || 0),
            trades: (r) => Number(r.trades || 0),
        },
    });

    return (
        <div ref={cardRef} className="border border-border bg-card rounded overflow-hidden" data-testid="agency-leaderboard">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-primary" />
                    <div className="overline">Private leaderboard · {total} trader{total === 1 ? "" : "s"}</div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="inline-flex rounded-md border border-border overflow-hidden text-xs">
                        <ScopeTab active={scope === "employees"} onClick={() => setScope("employees")}
                            label="Employees" testId="lb-scope-employees" />
                        <ScopeTab active={scope === "clients"} onClick={() => setScope("clients")}
                            label="Clients" testId="lb-scope-clients" />
                        <ScopeTab active={scope === "all"} onClick={() => setScope("all")}
                            label="All" testId="lb-scope-all" />
                    </div>
                    <button
                        type="button"
                        onClick={exportPng}
                        data-testid="lb-export-png"
                        title="Export as PNG (shareable in WhatsApp)"
                        className="inline-flex items-center gap-1 text-xs border border-border rounded px-2 py-1.5 hover:bg-secondary/40"
                    >
                        <Download className="w-3.5 h-3.5" />
                        PNG
                    </button>
                </div>
            </div>

            {/* Criterion picker — pill chips */}
            <div className="px-4 py-2 border-b border-border bg-secondary/20 flex items-center gap-1.5 flex-wrap"
                 data-testid="lb-criteria">
                {CRITERIA.map((c) => (
                    <button
                        key={c.key}
                        type="button"
                        onClick={() => setCriterion(c.key)}
                        title={c.desc}
                        data-testid={`lb-criterion-${c.key}`}
                        className={
                            "text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded-full border transition-colors " +
                            (criterion === c.key
                                ? "bg-primary text-primary-foreground border-primary"
                                : "border-border text-muted-foreground hover:border-primary/50 hover:text-primary")
                        }
                    >
                        {c.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : rows.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground" data-testid="lb-empty">
                    <Users className="w-6 h-6 mx-auto mb-2 opacity-50" />
                    No traders match this scope yet.
                </div>
            ) : (
                <table className="w-full text-sm">
                    <thead className="bg-secondary/40 text-xs uppercase tracking-widest text-muted-foreground">
                        <tr>
                            <SortableTh label="#" sortKey="rank" currentKey={sortKey} direction={direction} onSort={requestSort} className="w-14" />
                            <SortableTh label="Trader" sortKey="name" currentKey={sortKey} direction={direction} onSort={requestSort} />
                            <SortableTh label="Type" sortKey="type" currentKey={sortKey} direction={direction} onSort={requestSort} />
                            <SortableTh label={activeCriterion.label} sortKey="metric" currentKey={sortKey} direction={direction} onSort={requestSort} align="right" />
                            <SortableTh label="Days" sortKey="unique_days" currentKey={sortKey} direction={direction} onSort={requestSort} align="right" className="hidden md:table-cell" />
                            <SortableTh label="Trades" sortKey="trades" currentKey={sortKey} direction={direction} onSort={requestSort} align="right" className="hidden md:table-cell" />
                        </tr>
                    </thead>
                    <tbody data-testid="lb-tbody">
                        {sortedRows.map((r) => (
                            <tr key={r.user_id}
                                className={"border-t border-border " + (r.rank === 1 ? "bg-primary/5" : "")}
                                data-testid={`lb-row-${r.user_id}`}>
                                <td className="px-4 py-2 font-mono font-bold">
                                    {r.rank === 1 ? "🥇" : r.rank === 2 ? "🥈" : r.rank === 3 ? "🥉" : `#${r.rank}`}
                                </td>
                                <td className="px-4 py-2">{r.name}</td>
                                <td className="px-4 py-2">
                                    <span className={"px-2 py-0.5 rounded text-[10px] font-bold tracking-wide " + (
                                        r.client_type === "employee"
                                            ? "bg-accent/10 text-accent border border-accent/30"
                                            : "bg-secondary text-muted-foreground border border-border"
                                    )}>
                                        {r.client_type === "employee" ? "EMPLOYEE" : "CLIENT"}
                                    </span>
                                </td>
                                <td className={"px-4 py-2 text-right font-mono " + (
                                    criterion === "net_profit" ? toneClassName(signTone(r.net_profit, 0)) : ""
                                )}>
                                    {activeCriterion.format(r[criterion])}
                                </td>
                                <td className="px-4 py-2 text-right hidden md:table-cell mono">{r.unique_days}</td>
                                <td className="px-4 py-2 text-right hidden md:table-cell mono">{r.trades}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function ScopeTab({ active, onClick, label, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={
                "px-3 py-1.5 font-semibold border-r border-border last:border-r-0 " +
                (active
                    ? "bg-primary text-primary-foreground"
                    : "bg-transparent hover:bg-secondary/40 text-muted-foreground")
            }
        >
            {label}
        </button>
    );
}
